import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import { can } from '../permissions';
import {
  tasksList, taskGet, taskAssignees, taskCreate, taskUpdate, taskDelete, taskAddComment,
} from '../api';

// Board columns (status) + priorities, each: [key, ar, en, color].
const COLUMNS = [
  ['todo', 'قائمة المهام', 'To do', '#8b8fa3'],
  ['in_progress', 'قيد التنفيذ', 'In progress', '#3b82f6'],
  ['in_review', 'قيد المراجعة', 'In review', '#f59e0b'],
  ['done', 'مكتمل', 'Done', '#22c55e'],
];
const PRIORITIES = [
  ['urgent', 'عاجل', 'Urgent', '#ef4444'],
  ['high', 'مرتفع', 'High', '#f97316'],
  ['medium', 'متوسط', 'Medium', '#3b82f6'],
  ['low', 'منخفض', 'Low', '#8b8fa3'],
];
const prio = (k) => PRIORITIES.find(p => p[0] === k) || PRIORITIES[2];
const initials = (name) => (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

export default function Tasks() {
  const { lang, isRTL } = useLanguage();
  const { user } = useAuth();
  const ar = lang === 'ar';
  const t = (a, e) => (ar ? a : e);
  const canCreate = can(user, 'tasks.create');
  const canEdit = can(user, 'tasks.edit');
  const canDelete = can(user, 'tasks.delete');

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [filters, setFilters] = useState({ assignee: '', priority: '', search: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search.trim()), 350);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const load = useCallback(async () => {
    setLoading(true); setFailed(false);
    const res = await tasksList({ assignee: filters.assignee, priority: filters.priority, search: debouncedSearch });
    if (res.error) { setFailed(true); setTasks([]); } else { setTasks(res.tasks || []); }
    setLoading(false);
  }, [filters.assignee, filters.priority, debouncedSearch]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { taskAssignees().then(r => setAssignees(r.assignees || [])); }, []);

  const byStatus = useMemo(() => {
    const m = { todo: [], in_progress: [], in_review: [], done: [] };
    tasks.forEach(x => { (m[x.status] || m.todo).push(x); });
    return m;
  }, [tasks]);

  const moveTo = async (id, status) => {
    const task = tasks.find(x => x.id === id);
    if (!task || task.status === status) return;
    setTasks(ts => ts.map(x => x.id === id ? { ...x, status } : x)); // optimistic
    const res = await taskUpdate(id, { status });
    if (res.error) load();
  };

  const overdue = (task) => task.due_date && task.status !== 'done' && new Date(task.due_date) < new Date(new Date().toDateString());
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(ar ? 'ar-EG' : 'en-GB', { day: 'numeric', month: 'short' }) : '';

  const inp = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.82rem' };
  const hasFilters = filters.assignee || filters.priority || filters.search;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 1280, margin: '0 auto' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={filters.search} onChange={e => setFilters(p => ({ ...p, search: e.target.value }))} placeholder={t('بحث في المهام…', 'Search tasks…')} style={{ ...inp, flex: '1 1 200px', minWidth: 160 }} />
        {/* Only admin/manager oversee the whole board — everyone else only sees their own tasks. */}
        {(user?.role === 'admin' || user?.role === 'manager') && (
          <select value={filters.assignee} onChange={e => setFilters(p => ({ ...p, assignee: e.target.value }))} style={inp}>
            <option value="">{t('كل المكلّفين', 'All assignees')}</option>
            {assignees.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <select value={filters.priority} onChange={e => setFilters(p => ({ ...p, priority: e.target.value }))} style={inp}>
          <option value="">{t('كل الأولويات', 'All priorities')}</option>
          {PRIORITIES.map(([k, a, e]) => <option key={k} value={k}>{t(a, e)}</option>)}
        </select>
        {hasFilters && (
          <button onClick={() => setFilters({ assignee: '', priority: '', search: '' })} style={{ ...inp, cursor: 'pointer', fontWeight: 600 }}>{t('مسح', 'Clear')}</button>
        )}
        <div style={{ flex: 1 }} />
        {canCreate && (
          <button onClick={() => setShowNew(true)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
            + {t('مهمة جديدة', 'New task')}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 50, textAlign: 'center', color: 'var(--text-secondary)' }}>…</div>
      ) : failed ? (
        <div style={{ padding: 50, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('تعذّر تحميل المهام', 'Could not load tasks')}
          <div style={{ marginTop: 12 }}>
            <button onClick={load} style={{ padding: '7px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer' }}>{t('إعادة المحاولة', 'Retry')}</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLUMNS.length}, minmax(230px, 1fr))`, gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
          {COLUMNS.map(([key, aLbl, eLbl, color]) => (
            <div
              key={key}
              onDragOver={(e) => { if (canEdit && dragId != null) { e.preventDefault(); setOverCol(key); } }}
              onDragLeave={() => setOverCol(c => (c === key ? null : c))}
              onDrop={(e) => { e.preventDefault(); if (canEdit && dragId != null) moveTo(dragId, key); setDragId(null); setOverCol(null); }}
              style={{ background: overCol === key ? 'var(--bg-primary)' : 'var(--bg-secondary)', border: `1px solid ${overCol === key ? color : 'var(--border)'}`, borderRadius: 12, padding: 10, minHeight: 120, transition: 'border-color .12s' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '2px 4px' }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
                <span style={{ fontWeight: 800, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{t(aLbl, eLbl)}</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', background: 'var(--bg-primary)', borderRadius: 20, padding: '1px 8px', fontWeight: 700 }}>{byStatus[key].length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {byStatus[key].length === 0 ? (
                  <div style={{ padding: '14px 6px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{t('لا مهام', 'No tasks')}</div>
                ) : byStatus[key].map(task => {
                  const p = prio(task.priority);
                  return (
                    <div
                      key={task.id}
                      draggable={canEdit}
                      onDragStart={() => setDragId(task.id)}
                      onDragEnd={() => { setDragId(null); setOverCol(null); }}
                      onClick={() => setOpenId(task.id)}
                      style={{ background: 'var(--bg-card, var(--bg-primary))', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 11px', cursor: 'pointer', opacity: dragId === task.id ? 0.5 : 1, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
                    >
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.66rem', fontWeight: 800, color: p[3], background: p[3] + '1e', borderRadius: 6, padding: '1px 7px' }}>{t(p[1], p[2])}</span>
                        {overdue(task) && <span style={{ fontSize: '0.66rem', fontWeight: 800, color: '#ef4444', background: '#ef44441e', borderRadius: 6, padding: '1px 7px' }}>{t('متأخرة', 'Overdue')}</span>}
                      </div>
                      <div style={{ fontSize: '0.86rem', fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4, marginBottom: 8 }}>{task.title}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', fontSize: '0.72rem' }}>
                          {task.due_date && <span style={{ color: overdue(task) ? '#ef4444' : 'var(--text-secondary)' }}>🗓 {fmtDate(task.due_date)}</span>}
                          {Number(task.comment_count) > 0 && <span>💬 {task.comment_count}</span>}
                        </div>
                        {task.assigned_to ? (
                          <span title={task.assigned_to} style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--accent)', color: '#fff', fontSize: '0.66rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials(task.assigned_to)}</span>
                        ) : (
                          <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{t('غير مُسند', 'Unassigned')}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewTaskModal
          assignees={assignees} t={t} ar={ar} isRTL={isRTL} me={user?.name}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
      {openId != null && (
        <TaskModal
          id={openId} assignees={assignees} t={t} ar={ar} isRTL={isRTL} me={user?.name}
          canEdit={canEdit} canDelete={canDelete}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ── Modal shell ──
function Modal({ children, onClose, isRTL, wide }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', zIndex: 1000, overflowY: 'auto' }}>
      <div dir={isRTL ? 'rtl' : 'ltr'} onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: wide ? 640 : 480, padding: 20 }}>
        {children}
      </div>
    </div>
  );
}

const modalInput = { width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.88rem', boxSizing: 'border-box' };
const modalLabel = { fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 5, display: 'block' };

function NewTaskModal({ assignees, t, isRTL, me, onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', description: '', assigned_to: '', priority: 'medium', status: 'todo', due_date: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const save = async () => {
    setErr('');
    if (form.title.trim().length < 2) { setErr(t('العنوان مطلوب', 'Title is required')); return; }
    setBusy(true);
    const res = await taskCreate(form);
    setBusy(false);
    if (res.error) { setErr(t('تعذّر الإنشاء، حاول مجددًا', 'Could not create, try again')); return; }
    onCreated();
  };
  return (
    <Modal onClose={onClose} isRTL={isRTL}>
      <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 16 }}>{t('مهمة جديدة', 'New task')}</div>
      {err && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: '0.82rem', color: 'var(--error, #dc2626)' }}>{err}</div>}
      <div style={{ marginBottom: 12 }}>
        <label style={modalLabel}>{t('العنوان', 'Title')}</label>
        <input autoFocus value={form.title} onChange={e => u('title', e.target.value)} style={modalInput} placeholder={t('ماذا يجب إنجازه؟', 'What needs to be done?')} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={modalLabel}>{t('الوصف', 'Description')}</label>
        <textarea value={form.description} onChange={e => u('description', e.target.value)} rows={3} style={{ ...modalInput, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
        <div>
          <label style={modalLabel}>{t('المكلّف', 'Assignee')}</label>
          <select value={form.assigned_to} onChange={e => u('assigned_to', e.target.value)} style={modalInput}>
            <option value="">{t('غير مُسند', 'Unassigned')}</option>
            {me && !assignees.includes(me) && <option value={me}>{me}</option>}
            {assignees.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label style={modalLabel}>{t('الأولوية', 'Priority')}</label>
          <select value={form.priority} onChange={e => u('priority', e.target.value)} style={modalInput}>
            {PRIORITIES.map(([k, a, e]) => <option key={k} value={k}>{t(a, e)}</option>)}
          </select>
        </div>
        <div>
          <label style={modalLabel}>{t('الحالة', 'Status')}</label>
          <select value={form.status} onChange={e => u('status', e.target.value)} style={modalInput}>
            {COLUMNS.map(([k, a, e]) => <option key={k} value={k}>{t(a, e)}</option>)}
          </select>
        </div>
        <div>
          <label style={modalLabel}>{t('تاريخ الاستحقاق', 'Due date')}</label>
          <input type="date" value={form.due_date} onChange={e => u('due_date', e.target.value)} style={modalInput} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>{t('إلغاء', 'Cancel')}</button>
        <button disabled={busy} onClick={save} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1 }}>{t('إنشاء', 'Create')}</button>
      </div>
    </Modal>
  );
}

function TaskModal({ id, assignees, t, ar, isRTL, me, canEdit, canDelete, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [form, setForm] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedTick, setSavedTick] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setFailed(false);
    const res = await taskGet(id);
    if (res.error || !res.task) { setFailed(true); } else {
      setData(res);
      setForm({ title: res.task.title, description: res.task.description || '', status: res.task.status, priority: res.task.priority, assigned_to: res.task.assigned_to || '', due_date: res.task.due_date ? String(res.task.due_date).slice(0, 10) : '' });
    }
    setLoading(false);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (fields) => {
    if (!canEdit) return;
    setForm(f => ({ ...f, ...fields }));
    setBusy(true);
    const res = await taskUpdate(id, fields);
    setBusy(false);
    if (!res.error) { setSavedTick(true); setTimeout(() => setSavedTick(false), 1200); onChanged(); load(); }
  };
  const saveTitle = async () => { if (form.title.trim().length >= 2 && form.title !== data.task.title) await patch({ title: form.title.trim() }); };
  const saveDesc = async () => { if (form.description !== (data.task.description || '')) await patch({ description: form.description }); };
  const addComment = async () => {
    const msg = comment.trim();
    if (!msg) return;
    setBusy(true);
    const res = await taskAddComment(id, msg);
    setBusy(false);
    if (!res.error) { setComment(''); load(); onChanged(); }
  };
  const remove = async () => {
    if (!window.confirm(t('حذف هذه المهمة نهائيًا؟', 'Delete this task permanently?'))) return;
    const res = await taskDelete(id);
    if (!res.error) { onChanged(); onClose(); }
  };

  const fmt = (ts) => new Date(ts).toLocaleString(ar ? 'ar-EG' : 'en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const activityText = (a) => {
    if (a.type === 'created') return t('أنشأ المهمة', 'created the task');
    if (a.type === 'status') return t(`غيّر الحالة (${a.detail})`, `moved status (${a.detail})`);
    if (a.type === 'assigned') return t(`أسند إلى ${a.detail}`, `assigned to ${a.detail}`);
    if (a.type === 'priority') return t(`عيّن الأولوية ${a.detail}`, `set priority ${a.detail}`);
    return a.detail;
  };

  return (
    <Modal onClose={onClose} isRTL={isRTL} wide>
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>…</div>
      ) : failed ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {t('تعذّر تحميل المهمة', 'Could not load task')}
          <div style={{ marginTop: 12 }}><button onClick={load} style={{ padding: '7px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', cursor: 'pointer' }}>{t('إعادة', 'Retry')}</button></div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
            <input value={form.title} disabled={!canEdit} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} onBlur={saveTitle}
              style={{ flex: 1, fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-primary)', background: 'transparent', border: '1px solid transparent', borderRadius: 8, padding: '6px 8px' }}
              onFocus={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.background = 'var(--bg-primary)'; }} />
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1, padding: 4 }}>×</button>
          </div>

          {/* Attribute row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
            <div>
              <label style={modalLabel}>{t('الحالة', 'Status')}</label>
              <select value={form.status} disabled={!canEdit} onChange={e => patch({ status: e.target.value })} style={modalInput}>
                {COLUMNS.map(([k, a, e]) => <option key={k} value={k}>{t(a, e)}</option>)}
              </select>
            </div>
            <div>
              <label style={modalLabel}>{t('الأولوية', 'Priority')}</label>
              <select value={form.priority} disabled={!canEdit} onChange={e => patch({ priority: e.target.value })} style={modalInput}>
                {PRIORITIES.map(([k, a, e]) => <option key={k} value={k}>{t(a, e)}</option>)}
              </select>
            </div>
            <div>
              <label style={modalLabel}>{t('المكلّف', 'Assignee')}</label>
              <select value={form.assigned_to} disabled={!canEdit} onChange={e => patch({ assigned_to: e.target.value })} style={modalInput}>
                <option value="">{t('غير مُسند', 'Unassigned')}</option>
                {me && !assignees.includes(me) && <option value={me}>{me}</option>}
                {assignees.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label style={modalLabel}>{t('تاريخ الاستحقاق', 'Due date')}</label>
              <input type="date" value={form.due_date} disabled={!canEdit} onChange={e => patch({ due_date: e.target.value })} style={modalInput} />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={modalLabel}>{t('الوصف', 'Description')}</label>
            <textarea value={form.description} disabled={!canEdit} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} onBlur={saveDesc} rows={3}
              style={{ ...modalInput, resize: 'vertical', fontFamily: 'inherit' }} placeholder={canEdit ? t('أضف تفاصيل…', 'Add details…') : ''} />
          </div>

          {/* Comments */}
          <div style={{ fontWeight: 800, fontSize: '0.85rem', marginBottom: 8 }}>{t('التعليقات', 'Comments')} <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>({data.comments.length})</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, maxHeight: 200, overflowY: 'auto' }}>
            {data.comments.length === 0 ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', padding: '4px 2px' }}>{t('لا تعليقات بعد', 'No comments yet')}</div>
            ) : data.comments.map(c => (
              <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <span style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--accent)', color: '#fff', fontSize: '0.66rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initials(c.author)}</span>
                <div style={{ flex: 1, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10, padding: '7px 10px' }}>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.78rem' }}>{c.author || '—'}</span>
                    <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{fmt(c.created_at)}</span>
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{c.message}</div>
                </div>
              </div>
            ))}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input value={comment} onChange={e => setComment(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
                placeholder={t('اكتب تعليقًا…', 'Write a comment…')} style={{ ...modalInput, flex: 1 }} />
              <button disabled={busy || !comment.trim()} onClick={addComment} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: comment.trim() ? 'pointer' : 'not-allowed', opacity: comment.trim() ? 1 : 0.6, whiteSpace: 'nowrap' }}>{t('إرسال', 'Send')}</button>
            </div>
          )}

          {/* Activity */}
          {data.activity.length > 0 && (
            <details style={{ marginBottom: 8 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{t('السجل', 'Activity')} ({data.activity.length})</summary>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
                {data.activity.map(a => (
                  <div key={a.id} style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{a.actor || '—'}</span> {activityText(a)} · {fmt(a.created_at)}
                  </div>
                ))}
              </div>
            </details>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
              {savedTick ? t('✓ تم الحفظ', '✓ Saved') : t(`أنشأها ${data.task.created_by || '—'}`, `Created by ${data.task.created_by || '—'}`)}
            </span>
            {canDelete && <button onClick={remove} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid rgba(239,68,68,0.4)', background: 'transparent', color: '#ef4444', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>{t('حذف', 'Delete')}</button>}
          </div>
        </>
      )}
    </Modal>
  );
}
